import React, { useCallback, useContext, useEffect } from 'react'
import { CommonActions, RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import pluralize from 'pluralize'
import { Divider } from '@ui-kitten/components'
import { CheckoutContainer } from '@src/screens/Marketplace/components/checkout/CheckoutContainer'
import { ProductDescription } from '@src/screens/Marketplace/components/checkout/ProductDescription'
import { ProductPayment } from '@src/screens/Marketplace/components/checkout/ProductPayment'
import { AddressForm, AddressType } from '@src/screens/Marketplace/components/checkout/AddressForm'
import { CommonAuthorizedStackParamList } from '@src/navigation/types'
import { usePurchaseOneTimePayment } from '@screens/Marketplace/utils/hooks'
import { BillingProduct } from '@src/types'
import { NavigatorContext } from '@utils'
import { userSelector } from '@src/selectors/app'
import { LoadingContainer } from '@src/screens/Common/containers/LoadingContainer'
import { useShouldShowTwoSteloUnits } from '@src/screens/Marketplace/utils/hooks/useShouldShowTwoSteloUnits'
import { ProductSelection } from '@screens/Marketplace/types/types.ts'
import { formatCurrencyAmount } from '@src/screens/Marketplace/utils/utils'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { useSelection } from './useSelection'

const getPriceLabel = (selection: ProductSelection) => {
  if (selection.product.showUnitPrice && selection.product.units) {
    return `$${formatCurrencyAmount(selection.product.price / selection.product.units)}/${
      selection.product.cgm ? 'sensor' : 'unit'
    }`
  }
  return `$${selection.product.price * (selection.quantity ?? 1)}`
}

export const AddonCheckout = () => {
  useSecureScreen()

  const navigation = useNavigation()
  const route = useRoute<RouteProp<CommonAuthorizedStackParamList, 'AddonCheckout'>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const { nextScreen } = route.params

  const user = useSelector(userSelector)

  const {
    selection,
    lowestPricedProduct,
    fullPrice,
    requiresShipping,
    promotionCode,
    estimatedTax,
    loading,
  } = useSelection(route.params)

  const address = user?.address
  const addressType = requiresShipping ? AddressType.Shipping : AddressType.Billing
  const shouldNavigateToAddressScreen = !!address && !user.phoneNumber
  const shouldShowTwoSteloUnits = useShouldShowTwoSteloUnits(selection?.product)

  const navigateToAddressScreen = useCallback(() => {
    navigation.navigate('ChangeAddress', { addressType })
  }, [addressType, navigation])

  useEffect(() => {
    if (shouldNavigateToAddressScreen) {
      navigateToAddressScreen()
    }
  }, [navigateToAddressScreen, shouldNavigateToAddressScreen])

  const onSuccessNavigationAction = () => {
    if (nextScreen) {
      navigation.reset({
        index: 0,
        routes: [{ name: nextScreen.screen, params: nextScreen.params }],
      })
      return
    }

    navigation.dispatch((state) => {
      // Remove the last 2 routes (Checkout and ProductsSelection) from current list of routes
      const routes = state.routes.slice(0, -2)

      // navigate to OrderConfirmedScreen
      const newRoutes = [...routes, { name: 'OrderConfirmed' }]

      return CommonActions.reset({
        index: newRoutes.length - 1,
        routes: newRoutes,
      } as any)
    })
  }

  const { purchaseOneTimePayment } = usePurchaseOneTimePayment()

  const onCheckout = async () => {
    if (!selection) {
      return
    }

    enableLoader()

    try {
      await purchaseOneTimePayment({
        product: selection.product,
        quantity: selection.quantity,
        promotionCode,
      })
      onSuccessNavigationAction()
    } finally {
      disableLoader()
    }
  }

  if (loading || !selection || !lowestPricedProduct) {
    return <LoadingContainer />
  }

  const numberPieces = shouldShowTwoSteloUnits ? 2 : selection.quantity ?? 1

  return (
    <CheckoutContainer
      checkoutDisabled={!selection}
      checkoutButtonTitle="Confirm Order"
      onCheckout={onCheckout}
    >
      <ProductDescription
        key={selection.product.id}
        title={selection.product.title}
        description={pluralize('piece', numberPieces, true)}
        price={getPriceLabel(selection)}
      />
      <Divider />
      <ProductPayment
        product={lowestPricedProduct.key as BillingProduct}
        estimatedTax={estimatedTax}
        fullPrice={fullPrice}
        promotionCode={promotionCode}
      />
      <Divider appearance="large" />
      <AddressForm
        addressType={addressType}
        address={address}
        onEditAddressPress={navigateToAddressScreen}
      />
    </CheckoutContainer>
  )
}
